import React, { useState, useEffect } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { Alert, Grid, Box, Link, Typography, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import TextInput from '../../common/TextInput';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import FormWrapper from '../../common/StyledFormWrapper';
import { useNavigate } from 'react-router-dom';
import useDebounce from '../../../hooks/useDebounce';
import { CONSTANTS } from '../../../constants';
// import {CONSTANTS, GCAPTCHA_CLIENT_KEY} from '../../../constants'; // GCAPTCHA_CLIENT_KEY not needed anymore
import { checkInviteUser, otpVerificationApi, registerInvitedUser, registerNonInvitedUser } from '../../../api';
import PasswordChecklist from 'react-password-checklist';
import { capitalizeWords, validateEmail } from '../../../utils';
import sdlogo from '../../../images/sdlogo.svg';
import CheckBoxInput from '../../common/CheckBoxInput';
// import ReCAPTCHA from 'react-google-recaptcha';
// import ReCAPTCHA from 'react-google-recaptcha';
import useAuthStore from '../../../store/authStore';
import Modal from '../../common/Modal';
import useTranslations from '../../../hooks/useTranslation';
import { updateUserPreferedLangApi } from '../../../api';

const Register = () => {
  const { translate } = useTranslations();
  const [loading, setLoading] = useState(false);
  const [inviteCode, setInviteCode] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isValidPassword, setIsValidPassword] = useState(false);
  const [error, setError] = useState(null);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const debounceEmail = useDebounce(email, CONSTANTS.DEBOUNCE_TIMEOUT);
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState('');
  const [isConsent, setIsConsent] = useState(false);
  const searchParams = new URLSearchParams(window.location.search);
  const code = searchParams.get('inviteCode');
  // LEADER SELF-SIGNUP IS WITHDRAWN — deliberately, not half-finished.
  //
  // `/signup?org=<id>&role=leader` used to be offered from Draft Day ▸ Leaders and Open Signup.
  // It never worked: this page reads only `inviteCode` and ignores `org` and `role`, so anyone
  // arriving that way was created as an ordinary user and never appeared in the leader list.
  //
  // PO decision 2026-08-13: leaders are INVITED or hand-added by an admin, never self-serve —
  // a leader is expected to have completed Years 1–3, and the startup-church exception is the
  // admin adding them directly. The links are gone; this route is left reachable and LABELLED
  // rather than 404'd, so anyone who has the URL saved (or finds it on the demo box) gets an
  // answer instead of a form that silently does the wrong thing.
  //
  // Turning it back on is not "restore the link" — see
  // sdtests/memory/project_leader_self_signup.md for the three things that must each be true.
  const isLeaderSelfSignup = (searchParams.get('role') || '').toLowerCase() === 'leader';
  // const [captchaToken, setCaptchaToken] = useState(null);
  // const [isCaptchaVerified, setIsCaptchaVerfied] = useState(false);
  const { loginUser, setOrganization, updateUserPreferedLang } = useAuthStore(state => state);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState('LEADER');
  // let captchaRef;

  const checkInvitedUser = async signUpCode => {
    try {
      setLoading(true);
      const response = await checkInviteUser(signUpCode);
      if (response?.data?.status === 200 && Array.isArray(response?.data?.data) && response?.data?.data?.length > 0) {
        setUserRole(response?.data?.data[0]?.user_role);
        setEmail(response?.data?.data[0]?.email);
        setName(`${response?.data?.data[0]?.first_name || ''} ${response?.data?.data[0]?.last_name || ''}`);
        setLoading(false);
      } else {
        setInviteCode(null);
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.set('alreadySignedUp', 'true');
        navigate(`/login?${currentParams.toString()}`);
        setLoading(false);
      }
    } catch (error) {
      setInviteCode(null);
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.set('alreadySignedUp', 'true');
      navigate(`/login?${currentParams.toString()}`);
      setLoading(false);
    }
  };

  const orgId = searchParams.get('orgId') || searchParams.get('organization_id');

  const handleInvitedRegister = async () => {
    try {
      const fullName = name.trim();
      const storedLang = localStorage.getItem('selectedLang');
      let currentLangKey = 1;
      if (storedLang) {
        const matchedLang = Object.values(CONSTANTS.LANG).find(l => l.value === storedLang);
        if (matchedLang) currentLangKey = matchedLang.key;
      }

      const data = {
        first_name: fullName.split(' ')[0],
        last_name: fullName.split(' ')[1] || '',
        email: email.toLowerCase().trim(),
        password: password,
        consent_signed: isConsent,
        user_prefered_lang: currentLangKey,
        organization_id: orgId || null
      };
      setLoading(true);
      const response = await registerInvitedUser(inviteCode, data);
      if (response?.data?.status === 200) {
        const userData = response?.data?.data;

        userData.user_prefered_lang = currentLangKey;

        const organizations = userData?.organizations || [];
        const defaultOrganization = (orgId && organizations.find(org => Number(org.id) === Number(orgId)))
          || organizations.find(org => org?.deactivate_login !== 1)
          || organizations[0];
        if (defaultOrganization) {
          setOrganization(defaultOrganization);
        }

        loginUser(userData);

        if (userData?.id) {
          try {
            await updateUserPreferedLangApi(userData.id, currentLangKey);
          } catch (err) { }
        }

        // Reload the page if the language changed so translations apply perfectly
        const newLangValue = Object.values(CONSTANTS.LANG).find(l => l.key === currentLangKey)?.value;
        if (storedLang && storedLang !== newLangValue) {
          setTimeout(() => { window.location.reload(); }, 100);
        }
      }
      setLoading(false);
    } catch (error) {
      setError(error?.response?.data?.message || translate('Registration failed. Please try again.'));
      setLoading(false);
    }
  };

  const handleNonInvitedRegister = async () => {
    try {
      const fullName = name.trim();
      const storedLang = localStorage.getItem('selectedLang');
      let currentLangKey = 1;
      if (storedLang) {
        const matchedLang = Object.values(CONSTANTS.LANG).find(l => l.value === storedLang);
        if (matchedLang) currentLangKey = matchedLang.key;
      }

      const data = {
        first_name: fullName.split(' ')[0],
        last_name: fullName.split(' ')[1] || '',
        email: email.toLowerCase().trim(),
        password: password,
        consent_signed: isConsent,
        user_prefered_lang: currentLangKey
        // captcha_code: captchaToken  // Commented out - captcha not needed
      };
      setLoading(true);
      const response = await registerNonInvitedUser(data);
      if (response?.data?.status === 200) {
        setIsOtpSent(true);
      }
      setLoading(false);
    } catch (error) {
      setError(error?.response?.data?.message || translate('Registration failed. Please try again.'));
      setLoading(false);
      setIsOtpSent(false);
    }
  };

  const handleRegisteration = async () => {
    if (!name.trim()) {
      setError(translate("All fields are required!"));
      return;
    }
    if (!email || !password) {
      setError(translate("All fields are required!"));
      return;
    }
    if (!isValidPassword) {
      return;
    }
    if (!isConsent) {
      setError(translate("All fields are required!"));
      return;
    }
    if (inviteCode) {
      handleInvitedRegister();
    } else {
      handleNonInvitedRegister();
    }
  };

  const handleRoleChange = (event) => {
    const newRole = event.target.value;
    setSelectedRole(newRole);
  };

  const verifyOtpHandler = async () => {
    if (validateEmail(email) && isValidPassword && name && otp && otp.trim().length > 0) {
      try {
        const data = {
          email: email.toLowerCase().trim(),
          otp: otp
        };
        setLoading(true);
        const response = await otpVerificationApi(data);
        if (response?.data?.status === 200) {
          navigate('/login');
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    } else {
      setError(translate("All fields are required!"));
    }
  };

  const userTypeRole = role => {
    let namedRole = '';
    switch (role) {
      case 'ADMIN':
        namedRole = translate("Administrator");
        break;
      case 'DISCIPLE':
        namedRole = translate("Discipleship");
        break;
      case 'LEADER':
        namedRole = translate("Leadership");
        break;
    }
    return namedRole;
  };

  // const onCaptchaChange = value => {
  //   setError(null);
  //   setIsCaptchaVerfied(true);
  //   setCaptchaToken(value);
  // };

  // const handleCaptchaChange = () => {
  //   if (isCaptchaVerified) {
  //     captchaRef.reset();
  //     setCaptchaToken(null);
  //     setIsCaptchaVerfied(false);
  //   }
  // };

  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'move_disciple') {
      navigate(`/login${window.location.search}`, { replace: true });
      return;
    }
    setInviteCode(code);
    if (code) {
      checkInvitedUser(code);
    }
  }, []);

  return (
    <>
      <CssBaseline />
      <Box mt={1} px={3} overflow="auto">
        <Box display="flex" justifyContent="center" mb={1}>
          <img src={sdlogo} width="100" height="100" />
        </Box>
        {/* Reached via the withdrawn leader self-signup link — say so plainly rather than
            presenting a form that cannot do what the URL implies. See the note by
            `isLeaderSelfSignup` above. */}
        {isLeaderSelfSignup && (
          <Box
            role="status"
            aria-label="Not in Use Currently — Experimental"
            sx={{
              maxWidth: 520, mx: 'auto', mt: 2, mb: 1, p: '12px 16px', borderRadius: '10px',
              background: '#fdf8ec', border: '1px solid #e6d3a3', textAlign: 'left'
            }}
          >
            <Typography sx={{fontSize: 13.5, fontWeight: 700, color: '#9a6a12'}}>
              Not in Use Currently — Experimental
            </Typography>
            <Typography sx={{fontSize: 13, color: '#6b5310', mt: 0.5, lineHeight: 1.5}}>
              Leaders do not sign themselves up. Ask your church administrator to invite you —
              you will get an email with a link to accept. Signing up on this page will create an
              ordinary account, not a leader one.
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            width: '100%',
            maxWidth: { xs: '100%', sm: 412 },
            marginInline: 'auto'
          }}
        >
          <FormWrapper
            subHeadingText={{
              text: (
                <>
                  {translate("Already Registered?")} <span style={{ textDecoration: 'underline' }}>{translate("Log in here")}</span>
                </>
              ),
              onClick: () => navigate('/login')
            }}
            buttonInputs={selectedRole === 'LEADER' ? [
              {
                text: isOtpSent ? translate("Verify OTP") : translate("Sign Up"),
                onClick: isOtpSent ? verifyOtpHandler : handleRegisteration
              }
            ] : []}
            headingText={translate("Create Your Account", { role: userTypeRole(userRole) })}
            loading={loading}
            disabled={loading}
          >
            {/* { !isOtpSent && !code && ( 
          <Grid item xs={12} marginBottom={1}>
               <RadioGroup
                 row
                 value={selectedRole}
                 onChange={handleRoleChange}
                 sx={{ mb: 2 }}
               >
                 <FormControlLabel 
                   value="LEADER" 
                   control={<Radio />} 
                   label={translate("Leader")} 
                 />
                 <FormControlLabel 
                   value="DISCIPLE" 
                   control={<Radio />} 
                   label={translate("Disciple")} 
                 />
               </RadioGroup>
             </Grid>
          )} */}

            {/* Only show form fields for LEADER role */}
            {selectedRole === 'LEADER' && (
              <Grid container spacing={1}>
                <Grid item xs={12} marginBottom={1}>
                  <TextInput
                    autoComplete="off"
                    label={translate("Name")}
                    placeholder={translate("Name")}
                    value={name}
                    maxLength={CONSTANTS.MAX_GROUP_LENGTH}
                    onChange={e => {
                      // handleCaptchaChange();
                      setName(String(capitalizeWords(e.target.value)).replace(CONSTANTS.REGEX.NAME_REGEX, ''));
                      setError(null);
                    }}
                    labelVariant="body2"
                    disabled={isOtpSent}
                  />
                </Grid>
                <Grid item xs={12} marginBottom={1}>
                  <TextInput
                    disabled={inviteCode || isOtpSent ? true : false}
                    value={email}
                    placeholder={translate("Email")}
                    autoComplete="off"
                    label={translate("Email")}
                    isAllowSpecialChar={true}
                    onChange={e => {
                      // handleCaptchaChange();
                      setEmail(String(e.target.value).replace(/\s+/g, ''));
                    }}
                    error={debounceEmail && !validateEmail(email)}
                    helperText={debounceEmail && !validateEmail(email) ? CONSTANTS.MESSAGES.INVALID_EMAIL : ''}
                    labelVariant="body2"
                  />
                </Grid>
                <Grid item xs={12} marginBottom={1}>
                  <div className="relative_container">
                    <TextInput
                      autoComplete="off"
                      label={translate("Password")}
                      value={password}
                      placeholder={translate("Password")}
                      onChange={e => {
                        // handleCaptchaChange();
                        setPassword(e.target.value);
                        setError(null);
                      }}
                      isAllowSpecialChar={true}
                      type={showPassword ? 'text' : 'password'}
                      labelVariant="body2"
                      disabled={isOtpSent}
                    />
                    {showPassword ? <VisibilityIcon onClick={() => setShowPassword(false)} className="eye_icon" /> : <VisibilityOffIcon onClick={() => setShowPassword(true)} className="eye_icon" />}
                  </div>
                  <PasswordChecklist
                    rules={['minLength', 'specialChar', 'number', 'capital', 'lowercase']}
                    minLength={8}
                    value={password}
                    validColor="#FFFFFF"
                    invalidColor="rgba(255,255,255,0.45)"
                    style={{ textAlign: 'start' }}
                    messages={{
                      minLength: translate("Should be minimum 8 characters."),
                      specialChar: translate("Should contain special character."),
                      number: translate("Should contain number."),
                      capital: translate("Should contain uppercase letter."),
                      lowercase: translate("Should contain lowercase letter.")
                    }}
                    onChange={isValid => setIsValidPassword(isValid)}
                  />
                </Grid>

                {isOtpSent && (
                  <Grid item xs={12} marginBottom={1}>
                    <TextInput
                      value={otp}
                      placeholder={translate("OTP")}
                      autoComplete="off"
                      label={translate("OTP")}
                      onChange={e => {
                        setError(null);
                        setOtp(String(e.target.value).replace(!CONSTANTS.REGEX.ONLY_APHANUMERIC, ''));
                      }}
                      labelVariant="body2"
                      maxLength={4}
                      isAllowSpecialChar={true}
                    />
                  </Grid>
                )}
                {/* Captcha component commented out - not needed for registration */}
                {/* {!code && (
              <Grid item xs={12}>
                <ReCAPTCHA
                  ref={r => {
                    if (r) {
                      return (captchaRef = r);
                    }
                  }}
                  className="captcha-item"
                  theme="dark"
                  sitekey={GCAPTCHA_CLIENT_KEY}
                  onChange={onCaptchaChange}
                />
              </Grid>
            )} */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'start' }}>
                    <Box sx={{ position: 'relative', marginRight: '10px', flexShrink: 0 }}>
                      <Box
                        component="input"
                        type="checkbox"
                        checked={isConsent}
                        onChange={e => {
                          setError(null);
                          setIsConsent(e.target.checked);
                        }}
                        sx={{
                          appearance: 'none',
                          width: 18,
                          height: 18,
                          border: '1px solid #dedede',
                          backgroundColor: isConsent ? '#5b515184' : 'white',
                          borderRadius: '1px',
                          display: 'inline-block',
                          position: 'relative',
                          cursor: 'pointer',
                          margin: 0,
                          marginTop: 0.5,
                          '&:checked::after': {
                            content: '""',
                            position: 'absolute',
                            top: 2,
                            left: 5,
                            width: 6,
                            height: 10,
                            border: 'solid white',
                            borderWidth: '0 2px 2px 0',
                            transform: 'rotate(45deg)'
                          }
                        }}
                      />
                    </Box>
                    <Typography variant="h6" fontSize="1rem" sx={{ fontSize: '16px' }}>
                      {translate("I agree to the")}{' '}
                      <Link
                        sx={{ textDecoration: 'underline', cursor: 'pointer', color: 'inherit' }}
                        onClick={() => {
                          setModalOpen(true);
                        }}
                      >
                        {translate("Terms and Privacy Policy")}
                      </Link>
                    </Typography>
                  </Box>
                </Grid>
                <Modal modalbgColor={'white'} color='black' open={modalOpen} setOpen={setModalOpen} title={translate("Terms and Conditions for Sustainable Discipleship App")} titleStyles={{ fontWeight: 'bold', color: "black", fontSize: '20px' }} buttons={[{ name: translate("Close"), onClick: () => setModalOpen(false) }]}>
                  <Typography variant="h6" component="h4" color="black" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {translate("Effective Date:")} {CONSTANTS.APPLICATION_REGISTRATION_DATE}
                  </Typography>

                  <Typography color="black" variant="h6" component="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {translate("1. Acceptance of Terms")}
                  </Typography>
                  <Typography color="black" paragraph>{translate("By accessing or using the Sustainable Discipleship App, you agree to comply with and be bound by these Terms and Conditions ('Terms'). If you do not agree with these Terms, please do not use the App.")}</Typography>

                  {/* <Typography color="black" variant="h6" component="h4" gutterBottom sx={{fontWeight: 'bold'}}>
                  {translate("2. Eligibility")}
                </Typography>
                <Typography color="black" paragraph>{translate("The App is intended for use by individuals who are at least 13 years old. By using the App, you represent and warrant that you meet this age requirement.")}</Typography> */}

                  <Typography color="black" variant="h6" component="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {translate("2. Privacy and Data Collection")}
                  </Typography>
                  <Typography color="black" paragraph>
                    <Typography color="black" component="span" sx={{ fontWeight: 'bold' }}>
                      {translate("a. Anonymous Data Collection:")}{' '}
                    </Typography>
                    {translate("The App may collect anonymous data about your usage to help us improve the App. This data may include information such as your device type, operating system, and usage patterns. This data is collected anonymously and cannot be used to identify you personally.")}
                  </Typography>
                  <Typography color="black" paragraph>
                    <Typography color="black" component="span" sx={{ fontWeight: 'bold' }}>
                      {translate("b. Personal Data Collection:")}{' '}
                    </Typography>
                    {translate("If you choose to provide personal information, such as your name, email address, or phone number, this data will be used by the organization for internal purposes. This may include promoting church events, providing personalized content, and helping to improve your experience with the App.")}
                  </Typography>
                  <Typography color="black" paragraph>
                    <Typography color="black" component="span" sx={{ fontWeight: 'bold' }}>
                      {translate("c. Data Usage:")}{' '}
                    </Typography>
                    {translate("Personal information collected will not be sold or shared with third parties outside of our organization, except as required by law or to protect the rights, property, or safety of our users or others.")}
                  </Typography>

                  <Typography color="black" variant="h6" component="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {translate("3. Use of the App")}
                  </Typography>
                  <Typography color="black" paragraph>{translate("You agree to use the App only for lawful purposes and in a manner that does not infringe the rights of, restrict, or inhibit anyone else's use and enjoyment of the App.")}</Typography>

                  <Typography color="black" variant="h6" component="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {translate("4. User Accounts")}
                  </Typography>
                  <Typography color="black" paragraph>{translate("To access certain features of the App, you may be required to create an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.")}</Typography>

                  <Typography color="black" variant="h6" component="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {translate("5. Intellectual Property")}
                  </Typography>
                  <Typography color="black" paragraph>{translate("All content, trademarks, and data on the App, including but not limited to text, graphics, logos, icons, images, audio clips, and software, are the property of the Sustainable Discipleship or its content suppliers and protected by international intellectual property laws.")}</Typography>

                  <Typography color="black" variant="h6" component="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {translate("6. Limitation of Liability")}
                  </Typography>
                  <Typography color="black" paragraph>{translate("To the fullest extent permitted by law, the Sustainable Discipleship shall not be liable for any direct, indirect, incidental, special, or consequential damages resulting from the use or the inability to use the App or for the cost of procurement of substitute goods and services or resulting from any goods or services purchased or obtained or messages received or transactions entered into through the App or resulting from unauthorized access to or alteration of your transmissions or data.")}</Typography>

                  <Typography color="black" variant="h6" component="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {translate("7. Termination")}
                  </Typography>
                  <Typography color="black" paragraph>{translate("We reserve the right to terminate or suspend your access to the App, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.")}</Typography>

                  <Typography color="black" variant="h6" component="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {translate("8. Changes to Terms")}
                  </Typography>
                  <Typography color="black" paragraph>{translate("We may revise these Terms from time to time without notice. By continuing to access or use the App after those revisions become effective, you agree to be bound by the revised Terms.")}</Typography>

                  <Typography color="black" variant="h6" component="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {translate("9. Governing Law")}
                  </Typography>
                  <Typography color="black" paragraph>{translate("These Terms are governed by and construed in accordance with the laws of GA/USA, without regard to its conflict of law principles.")}</Typography>

                  <Typography color="black" variant="h6" component="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {translate("10. Contact Us")}
                  </Typography>
                  <Typography color="black" paragraph>
                    {translate("If you have any questions about these Terms, please contact us at")} <Link sx={{ textDecoration: 'underline', cursor: 'pointer', color: 'inherit' }} href="mailto:app@sustainable-discipleship.com">{CONSTANTS.CONTACT_URL}</Link>.
                  </Typography>
                </Modal>
                <Grid item xs={12} sx={{ height: '55px' }}>
                  {error && <Alert severity="error">{error}</Alert>}
                </Grid>
              </Grid>
            )}
            {/* Disciple warning message */}
            {selectedRole === 'DISCIPLE' && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {translate("Please contact the administrator as we only provide registration as a Leader for public users.")}
                </Alert>
              </Grid>
            )}
          </FormWrapper>
        </Box>
      </Box>
    </>
  );
};

export default Register;
